import GtagTracker from './gtag';
import GtmTracker from './gtm';
import GaTracker from './ga';
import GaqTracker from './gaq';
import DummyTracker from './dummy';
import logger from '../../logger';
import AnalyticsTracker from './interface';

/**
 * This returns a list of all AnalyticsTracker, their names and log-information
 * @param windowObj The window object that is used to access globals of tracking providers.
 * @param config Findologic config.
 * @returns {AnalyticsTracker[]} Get a list of all AnalyticsTracker and their names in a certain order
 */
function getAvailableTrackersAndLogInfos(windowObj, config) {
    return [
        { name: 'gtag', tracker: new GtagTracker(windowObj, config), logMsg: 'Using gtag for tracking.' },
        { name: 'gtm', tracker: new GtmTracker(windowObj, config), logMsg: 'Using GTM for tracking, because gtag is absent.' },
        { name: 'ga', tracker: new GaTracker(windowObj, config), logMsg: 'Using ga for tracking, because gtag and dataLayer are absent.' },
        {
            name: 'gaq',
            tracker: new GaqTracker(windowObj, config),
            logMsg: 'Using _gaq for tracking, because gtag, dataLayer and ga are absent.',
        },
    ];
}

/**
 * Iterates over each available track information that fulfills the condition in the callback function first
 * @param windowObj The window object that is used to access globals of tracking providers.
 * @param predicate This function is used to provide a condition on what a certain track implementation should be used
 * @param config Findologic config.
 * @returns {AnalyticsTracker} The tracker that can be expected to work in the current environment
 *      along with it's name and log-information
 */
function findTrackerInfoByPredicate(windowObj, predicate, config) {
    const trackersInfo = getAvailableTrackersAndLogInfos(windowObj, config);

    for (let index = 0; index < trackersInfo.length; index++) {
        const hasCallbackSucceeded = predicate(trackersInfo[index]);
        if (hasCallbackSucceeded) {
            return trackersInfo[index];
        }
    }
}

/**
 * If a selected AnalyticsTracker does not exist, try to get another one that exists in window object in a certain order
 * @param windowObj The window object that is used to access globals of tracking providers.
 * @param config Findologic config.
 * @returns {AnalyticsTracker} The tracker that can be expected to work in the current environment dictated by the window object
 */
function getTrackerByAutoDetection(windowObj, config) {
    const foundTrackerInfo = findTrackerInfoByPredicate(
        windowObj,
        function (availableTrackerInfo) {
            return availableTrackerInfo.tracker.isAvailable();
        },
        config
    );

    if (foundTrackerInfo) {
        logger.info(foundTrackerInfo.logMsg);
        return foundTrackerInfo.tracker;
    }

    logger.warn('Unable to track with GTM or GA. None of dataLayer, ga, and _gaq are present.');
    return new DummyTracker(windowObj, config);
}

/**
 * Try to get selected AnalyticsTracker from Service configuration, if the implementation exists in window object
 * @param selectedGATrackerName Holds the name of the AnalyticsTracker that should be used
 * @param windowObj The window object that is used to access globals of tracking providers.
 * @param config Findologic config.
 * @returns {AnalyticsTracker} The tracker that can be expected to work in the current environment dictated
 *      by the window object and selected in shop the settings.
 */
function getTrackerBySelectedImplementation(selectedGATrackerName, windowObj, config) {
    const foundTrackerInfo = findTrackerInfoByPredicate(
        windowObj,
        function (availableTrackerInfo) {
            return availableTrackerInfo.name === selectedGATrackerName && availableTrackerInfo.tracker.isAvailable();
        },
        config
    );

    if (foundTrackerInfo) {
        logger.info('Using selected ' + selectedGATrackerName + ' for tracking.');
        return foundTrackerInfo.tracker;
    }

    logger.info('Unable to track with selected ' + selectedGATrackerName + ' for tracking because it is absent.');
    return new DummyTracker(windowObj, config);
}

/**
 * Creates an instance of error tracker that fits the ones in the environment. If tracking is disabled, a
 * dummy is returned. The order of preference is GTM > ga() > _gaq > dummy.
 *
 * @param {{}} config Service configuration, which is used to look up whether tracking is enabled.
 * @param {{}} config.tracking
 * @param {{}} config.tracking.analytics
 * @param {string|undefined|null} config.tracking.analytics.implementationOverride
 * @param {boolean} config.trackMerchandisingFeatures True in case tracking is desired. If false, a dummy
 *      tracker is always returned.
 * @param {boolean} forceTracking If true, a tracker will be returned even if merchandising feature tracking has
 *      been disabled. This should only be used for search.
 * @param {Window} [windowObj] The window object that is used to access globals of tracking providers.
 * @param {Array} [windowObj.dataLayer] Possible Google Tag Manager queue.
 * @param {function} [windowObj.ga] Possible Google Analytics function.
 * @param {Array} [windowObj._gaq] Possible deprecated Google Analytics queue.
 * @returns {AnalyticsTracker} The tracker that can be expected to work in the current environment dictated by the window object.
 */
AnalyticsTracker.getInstance = function (config, forceTracking, windowObj) {
    if (typeof windowObj === 'undefined') {
        windowObj = window;
    }

    let implementation;

    if (config.trackMerchandisingFeatures || forceTracking) {
        const selectedGATrackerName =
            config.tracking && config.tracking.analytics ? config.tracking.analytics.implementationOverride : null;

        if (selectedGATrackerName === null) {
            implementation = getTrackerByAutoDetection(windowObj, config);
        } else {
            implementation = getTrackerBySelectedImplementation(selectedGATrackerName, windowObj, config);
        }
    } else {
        implementation = new DummyTracker(windowObj, config);
    }

    return implementation;
};

export default AnalyticsTracker;
